/**
 * Resume Studio — Guest-checkout B2C funnel at /resume.
 *
 * Two entry paths:
 *   - "I have a CV" (rewrite mode) → upload/paste → ATS score → NaukriDaddy AI rewrite → preview
 *   - "I'm building my first CV" (fresher mode) → guided form → NaukriDaddy AI generate → preview
 *
 * Tiers: Fresher ₹49 · Standard ₹99 · Pro ₹299 · Elite ₹799 · Top-up ₹599 for 10 revisions.
 * Preview PDF has both a bottom-half blur veil (server-side) AND a soft frontend blur
 * overlay on the last 40% of the iframe for anti-screenshot defense in depth.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Brand from "@/components/Brand";
import { API } from "@/lib/api";
import useSeo from "@/lib/useSeo";
import SeoMeta from "@/components/SeoMeta";
import { fireGadsConversion } from "@/lib/gtagConversions";
import {
    UploadSimple,
    FileText,
    Sparkle,
    ArrowRight,
    CheckCircle,
    Lightning,
    ShieldCheck,
    GraduationCap,
    Plus,
    Trash,
    LockKey,
    Download,
    ArrowsClockwise,
    Warning,
    MagnifyingGlass,
} from "@phosphor-icons/react";

// Extracted sub-components (V1.4.3–V1.4.5 refactor).
import MyCvsLookupModal from "../components/resume-studio/MyCvsLookupModal";
import ShareCVWidget from "../components/resume-studio/ShareCVWidget";
import PhotoUpload from "../components/resume-studio/PhotoUpload";
import EducationBlock from "../components/resume-studio/EducationBlock";
import StyleOptionsPanel from "../components/resume-studio/StyleOptionsPanel";
import PreviewAndPay from "../components/resume-studio/PreviewAndPay";
// V1.5.8 — Referral loop
import ReferralShareCard from "../components/resume-studio/ReferralShareCard";
import ReferralGiftBanner from "../components/resume-studio/ReferralGiftBanner";
import { computeDeviceFingerprint } from "../lib/deviceFingerprint";
import {
    Field, BackButton, BackStepButton, NextButton, StepDots,
    AtsScoreBadge, ItemCard, YearRangeInputs, loadRazorpay,
} from "../components/resume-studio/shared";
import { TIER_META } from "../components/resume-studio/constants";
import * as rsSession from "../lib/resumeStudioSession";

// --- Constants -----------------------------------------------------------
// TIER_META moved to /components/resume-studio/constants.js (V1.4.5 refactor).

// --- Root wizard ---------------------------------------------------------
export default function ResumeStudioPage() {
    const [phase, setPhase] = useState("chooser"); // chooser | rewrite | fresher | experienced | preview
    const [session, setSession] = useState(null);
    const [pricing, setPricing] = useState(null);
    const [prefill, setPrefill] = useState({ logged_in: false, data: {} });
    const [lastSession, setLastSession] = useState(null); // localStorage recovery banner
    const [showRecovery, setShowRecovery] = useState(false);
    // V1.5.8 — capture the friend's `?ref=CODE` for the gift banner + attach on session create.
    const [refCode, setRefCode] = useState(() => {
        try {
            const c = new URLSearchParams(window.location.search).get("ref");
            return c ? c.trim().toUpperCase() : null;
        } catch { return null; }
    });

    // Structured data — Product / SoftwareApplication for Resume Studio.
    // Deliberately no `AggregateRating`: Google guidelines require ratings
    // to reflect *per-user reviews*, not synthetic averages. Once we ship
    // a real review-collection flow we can layer that on.
    // V1.5.8.48 — SEO metadata now managed centrally by
    // <SeoMeta endpoint="/seo/page/resume-builder/meta"/> below. The
    // previous inline useSeo() call was removed here because it wrote
    // conflicting head tags (canonical + og:* + twitter:* + JSON-LD)
    // in parallel with SeoMeta, causing duplicate/conflicting metadata
    // (testing agent report 128 flagged this). Pricing offers +
    // featureList + FAQ are now part of the /api/seo/page/resume-builder/meta
    // response so nothing is lost.

    useEffect(() => {
        fetch(`${API}/resume-studio/pricing`)
            .then((r) => r.json())
            .then((d) => setPricing(d))
            .catch(() => setPricing(null));

        // Priority 2: candidate prefill (auth-aware; guests get empty).
        fetch(`${API}/resume-studio/prefill`, { credentials: "include" })
            .then((r) => (r.ok ? r.json() : { logged_in: false, data: {} }))
            .then((d) => setPrefill(d || { logged_in: false, data: {} }))
            .catch(() => {});

        // Priority 1: URL ?session=<id> → jump to preview.
        const params = new URLSearchParams(window.location.search);
        const sid = params.get("session");
        if (sid) {
            fetch(`${API}/resume-studio/session/${sid}`)
                .then((r) => (r.ok ? r.json() : null))
                .then((d) => {
                    if (d && d.session) {
                        setSession(d.session);
                        // Feb 18, 2026 — Prod bug guard: sessions without a
                        // completed AI rewrite (`preview_available=false`)
                        // can't render a preview PDF and previously showed a
                        // broken iframe / "prohibited-icon" placeholder.
                        // Route those back to the correct wizard phase so
                        // the user can finish the flow instead of hitting a
                        // dead-end.
                        if (d.session.preview_available === false) {
                            const mode = d.session.mode || "rewrite";
                            setPhase(mode === "fresher" ? "fresher"
                                    : mode === "experienced" ? "experienced"
                                    : "rewrite");
                        } else {
                            setPhase("preview");
                        }
                    }
                })
                .catch(() => {});
            return;
        }

        // localStorage session-recovery banner.
        const last = rsSession.loadLastSession();
        if (last && last.session_id) {
            fetch(`${API}/resume-studio/session/${last.session_id}`)
                .then((r) => (r.ok ? r.json() : null))
                .then((d) => {
                    if (d && d.session) {
                        setLastSession({ ...last, live: d.session });
                    } else {
                        rsSession.clearLastSession();
                    }
                })
                .catch(() => {});
        }
    }, []);

    // Persist session pointer whenever we advance in the flow.
    useEffect(() => {
        if (session && session.session_id && phase !== "chooser") {
            rsSession.saveLastSession(session, { phase });
        }
    }, [session, phase]);

    const startRewrite = () => setPhase("rewrite");
    const startFresher = () => setPhase("fresher");
    const startExperienced = () => setPhase("experienced");
    const backToChooser = () => {
        setSession(null);
        setPhase("chooser");
    };

    const resumeLast = () => {
        if (!lastSession?.live) return;
        setSession(lastSession.live);
        setPhase(lastSession.phase === "preview" ? "preview" : "preview");
        setLastSession(null);
    };
    const dismissRecovery = () => {
        rsSession.clearLastSession();
        setLastSession(null);
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#F7F9FC] via-white to-white text-[#0F172A]">
            <SeoMeta endpoint="/seo/page/resume-builder/meta" />
            <ResumeNavbar />
            <main className="max-w-6xl mx-auto px-4 md:px-6 pt-8 pb-16">
                {prefill.logged_in && phase === "chooser" && (
                    <div
                        data-testid="prefill-banner"
                        className="mb-4 flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2"
                    >
                        <CheckCircle weight="fill" size={14} />
                        Welcome back, {prefill.data?.name?.split(" ")[0] || "there"} — we'll pre-fill
                        your details in the wizard.
                    </div>
                )}
                {lastSession && phase === "chooser" && (
                    <div
                        data-testid="resume-recovery-banner"
                        className="mb-6 rounded-xl border border-cobalt-200 bg-cobalt-50/70 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                    >
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-cobalt-500 text-white grid place-items-center shrink-0">
                                <ArrowsClockwise weight="bold" size={18} />
                            </div>
                            <div>
                                <div className="text-sm font-semibold text-cobalt-900">
                                    Continue your CV
                                    {lastSession.live?.payment_status === "paid" && (
                                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full">
                                            <CheckCircle weight="fill" size={10} /> PAID
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-slate-600 mt-0.5">
                                    {lastSession.name || lastSession.email || "Draft"}
                                    {lastSession.target_role ? ` · ${lastSession.target_role}` : ""}
                                    {" · "}
                                    <span className="text-slate-500">
                                        started {rsSession.relativeTime(lastSession.updated_at)}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={dismissRecovery}
                                data-testid="resume-recovery-dismiss-btn"
                                className="text-xs font-medium text-slate-500 hover:text-slate-700 px-3 py-1.5"
                            >
                                Start fresh
                            </button>
                            <button
                                type="button"
                                onClick={resumeLast}
                                data-testid="resume-recovery-continue-btn"
                                className="text-xs font-semibold bg-cobalt-600 hover:bg-cobalt-700 text-white px-4 py-2 rounded-lg shadow-sm"
                            >
                                Continue →
                            </button>
                        </div>
                    </div>
                )}
                {phase === "chooser" && (
                    <>
                        <ReferralGiftBanner code={refCode} onDismiss={() => setRefCode(null)} />
                        <ChooserView
                            onRewrite={startRewrite}
                            onFresher={startFresher}
                            onExperienced={startExperienced}
                            pricing={pricing}
                        />
                    </>
                )}
                {phase === "rewrite" && (
                    <RewriteFlow
                        onSession={setSession}
                        onDone={() => setPhase("preview")}
                        onBack={backToChooser}
                        prefill={prefill.data}
                        refCode={refCode}
                    />
                )}
                {phase === "fresher" && (
                    <FresherFlow
                        onSession={setSession}
                        onDone={() => setPhase("preview")}
                        onBack={backToChooser}
                        prefill={prefill.data}
                        refCode={refCode}
                    />
                )}
                {phase === "experienced" && (
                    <ExperiencedFlow
                        onSession={setSession}
                        onDone={() => setPhase("preview")}
                        onBack={backToChooser}
                        prefill={prefill.data}
                        refCode={refCode}
                    />
                )}
                {phase === "preview" && session && (
                    <PreviewAndPay
                        session={session}
                        onSessionChange={setSession}
                        pricing={pricing}
                        onBack={backToChooser}
                    />
                )}
            </main>
            <ResumeFooter />
        </div>
    );
}

// --- Navbar --------------------------------------------------------------
function ResumeNavbar() {
    return (
        <header className="sticky top-0 z-40 backdrop-blur bg-white/80 border-b border-slate-200/70">
            <div className="max-w-6xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
                <Brand />
                <div className="flex items-center gap-3 text-sm">
                    <Link
                        to="/"
                        data-testid="resume-navbar-home-link"
                        className="text-slate-600 hover:text-slate-900"
                    >
                        Home
                    </Link>
                    <Link
                        to="/jobs"
                        data-testid="resume-navbar-jobs-link"
                        className="text-slate-600 hover:text-slate-900"
                    >
                        Jobs
                    </Link>
                    <span className="hidden md:inline text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                        New · Resume Studio
                    </span>
                </div>
            </div>
        </header>
    );
}

function ResumeFooter() {
    return (
        <footer className="border-t border-slate-200 bg-white">
            <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
                <span>© {new Date().getFullYear()} NaukriDaddy · Resume Studio · V1.4.1</span>
                <div className="flex items-center gap-4">
                    <Link to="/refund-policy" className="hover:text-slate-800">Refund policy</Link>
                    <Link to="/privacy" className="hover:text-slate-800">Privacy</Link>
                    <Link to="/contact" className="hover:text-slate-800">Contact</Link>
                </div>
            </div>
        </footer>
    );
}

// --- Chooser view --------------------------------------------------------
function ChooserView({ onRewrite, onFresher, onExperienced, pricing }) {
    const [showLookup, setShowLookup] = useState(false);
    return (
        <>
            {showLookup && <MyCvsLookupModal onClose={() => setShowLookup(false)} />}
            <section className="text-center py-8 md:py-12">
                <Link
                    to="/how-it-works"
                    data-testid="hero-how-it-works-link"
                    className="inline-flex items-center gap-2 text-xs font-semibold text-cobalt-700 bg-cobalt-50 hover:bg-cobalt-100 transition px-3 py-1 rounded-full mb-4"
                >
                    <Sparkle weight="fill" size={14} /> Powered by NaukriDaddy AI · <span className="underline decoration-dotted underline-offset-2">How it works</span>
                </Link>
                <h1
                    data-testid="resume-studio-hero-heading"
                    className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight"
                >
                    Get an ATS-cracking CV
                    <br />
                    <span className="bg-gradient-to-r from-cobalt-600 to-fuchsia-600 bg-clip-text text-transparent">
                        in under 90 seconds
                    </span>
                </h1>
                <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-base">
                    AI rewrites your CV for the exact role you want. Beats 90% of ATS filters. Pay once, download instantly.
                </p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><ShieldCheck weight="fill" size={14} className="text-emerald-600" /> Never invents facts</span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1"><Lightning weight="fill" size={14} className="text-amber-500" /> 12-second turnaround</span>
                    <span>·</span>
                    <span>Starts at ₹49</span>
                </div>
                <button
                    type="button"
                    onClick={() => setShowLookup(true)}
                    data-testid="my-cvs-open-btn"
                    className="mt-6 inline-flex items-center gap-1.5 text-xs font-medium text-cobalt-600 hover:text-cobalt-800 underline decoration-dotted underline-offset-4"
                >
                    <MagnifyingGlass size={12} weight="bold" />
                    Already have a CV with us? Find your past CVs →
                </button>
            </section>

            <section className="grid md:grid-cols-3 gap-4 md:gap-5 max-w-6xl mx-auto">
                <button
                    onClick={onRewrite}
                    data-testid="chooser-rewrite-btn"
                    className="group text-left rounded-2xl border border-slate-200 hover:border-cobalt-500 hover:shadow-xl transition-all bg-white p-6"
                >
                    <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-cobalt-50 text-cobalt-600 mb-4">
                        <FileText weight="duotone" size={26} />
                    </div>
                    <div className="text-base md:text-lg font-bold">I have a CV to improve</div>
                    <div className="text-sm text-slate-600 mt-1">
                        Upload your PDF or paste text. AI rewrites for your target role and boosts your ATS score.
                    </div>
                    <div className="mt-4 inline-flex items-center text-sm text-cobalt-600 font-semibold group-hover:gap-2 transition-all">
                        Start · from ₹99 <ArrowRight size={14} className="ml-1" />
                    </div>
                </button>

                <button
                    onClick={onFresher}
                    data-testid="chooser-fresher-btn"
                    className="group text-left rounded-2xl border border-slate-200 hover:border-emerald-500 hover:shadow-xl transition-all bg-white p-6"
                >
                    <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 mb-4">
                        <GraduationCap weight="duotone" size={26} />
                    </div>
                    <div className="text-base md:text-lg font-bold">I'm building my first CV</div>
                    <div className="text-sm text-slate-600 mt-1">
                        Fill a 2-minute form. AI writes your first-ever CV from scratch — freshers, interns, college students welcome.
                    </div>
                    <div className="mt-4 inline-flex items-center text-sm text-emerald-600 font-semibold group-hover:gap-2 transition-all">
                        Start · just ₹49 <ArrowRight size={14} className="ml-1" />
                    </div>
                </button>

                <button
                    onClick={onExperienced}
                    data-testid="chooser-experienced-btn"
                    className="group text-left rounded-2xl border border-slate-200 hover:border-purple-500 hover:shadow-xl transition-all bg-white p-6"
                >
                    <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-purple-50 text-purple-600 mb-4">
                        <Sparkle weight="duotone" size={26} />
                    </div>
                    <div className="text-base md:text-lg font-bold">
                        I'm experienced — build from scratch
                        <span className="ml-1.5 text-[9px] font-black px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 uppercase tracking-wider">
                            New
                        </span>
                    </div>
                    <div className="text-sm text-slate-600 mt-1">
                        2+ years of full-time experience? Add companies, roles, R&R bullets. AI structures + polishes end-to-end.
                    </div>
                    <div className="mt-4 inline-flex items-center text-sm text-purple-600 font-semibold group-hover:gap-2 transition-all">
                        Start · from ₹99 <ArrowRight size={14} className="ml-1" />
                    </div>
                </button>
            </section>

            {pricing && (
                <section className="mt-14">
                    <h2 className="text-lg font-bold text-center mb-6">All plans</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {pricing.tiers.map((t) => {
                            const meta = TIER_META[t.id] || {};
                            const isAmberTier = t.id === "elite";
                            const isPurpleTier = t.id === "pro";
                            const revBadgeCls = t.revisions === 0
                                ? "bg-slate-100 text-slate-600"
                                : isAmberTier
                                    ? "bg-amber-100 text-amber-800 ring-1 ring-amber-300"
                                    : isPurpleTier
                                        ? "bg-purple-100 text-purple-800 ring-1 ring-purple-300"
                                        : "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300";
                            return (
                                <div
                                    key={t.id}
                                    data-testid={`pricing-tier-${t.id}`}
                                    className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-md transition-shadow"
                                >
                                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                        {meta.label || t.id}
                                    </div>
                                    <div className="text-3xl font-black mt-1">
                                        ₹{t.price_inr}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-1">{meta.subtitle}</div>
                                    {/* Prominent revisions callout */}
                                    <div className={`mt-3 inline-flex items-center gap-1.5 text-xs font-black px-2.5 py-1 rounded-full ${revBadgeCls}`}>
                                        <ArrowsClockwise size={12} weight="bold" />
                                        {t.revisions === 0
                                            ? "No AI revisions"
                                            : `${t.revisions} AI ${t.revisions === 1 ? "revision" : "revisions"}`}
                                    </div>
                                    {/* Social proof — user requested Feb 13, 2026 */}
                                    {t.id === "pro" && (
                                        <div className="mt-2 text-[11px] text-purple-700 italic leading-relaxed">
                                            <span className="font-bold">68%</span> of Pro buyers use all 3 revisions on average
                                        </div>
                                    )}
                                    {t.id === "elite" && (
                                        <div className="mt-2 text-[11px] text-amber-800 italic leading-relaxed">
                                            <span className="font-bold">Executives iterate</span> — 6/10 revisions used on average
                                        </div>
                                    )}
                                    <ul className="mt-3 space-y-1.5 text-sm">
                                        {(meta.highlights || []).map((h) => (
                                            <li key={h} className="flex items-start gap-2">
                                                <CheckCircle
                                                    weight="fill"
                                                    size={14}
                                                    className="text-emerald-500 mt-0.5 flex-shrink-0"
                                                />
                                                <span>{h}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>
                    <p className="text-center text-xs text-slate-400 mt-4">
                        Ran out of revisions? Top up ₹599 for 10 more anytime.
                    </p>
                </section>
            )}
        </>
    );
}

// --- Rewrite flow (upload existing CV) -----------------------------------
function RewriteFlow({ onSession, onDone, onBack, prefill = {}, refCode = null }) {
    const [step, setStep] = useState(1); // 1=email+role, 2=upload/paste+rewrite
    const [email, setEmail] = useState(prefill.email || "");
    const [targetRole, setTargetRole] = useState(prefill.target_role || "");
    const [session, setSession] = useState(null);
    const [cvText, setCvText] = useState(prefill.cv_text || "");
    const [uploading, setUploading] = useState(false);
    const [running, setRunning] = useState(false);
    const [origAts, setOrigAts] = useState(null);
    const [newAts, setNewAts] = useState(null);
    const fileRef = useRef(null);

    const createSession = async () => {
        if (!email || !email.includes("@")) return toast.error("Enter a valid email");
        if (!targetRole || targetRole.length < 3) return toast.error("Enter your target role");
        try {
            // V1.5.8.2 — device fingerprint for referral anti-abuse (soft-fails to null).
            const device_fp = refCode ? await computeDeviceFingerprint() : null;
            const r = await fetch(`${API}/resume-studio/session`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email, target_role: targetRole, mode: "rewrite",
                    ...(refCode ? { referral_code: refCode, device_fp } : {}),
                }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.detail || "Session failed");
            setSession(d.session);
            setStep(2);
        } catch (e) {
            toast.error(e.message);
        }
    };

    const uploadPdf = async (file) => {
        if (!file || !session) return;
        setUploading(true);
        setOrigAts(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await fetch(`${API}/resume-studio/session/${session.session_id}/upload`, {
                method: "POST",
                body: fd,
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.detail || "Upload failed");
            setOrigAts(d.ats);
            toast.success(`Your CV scored ${d.ats.overall}/100 — let AI improve it.`);
        } catch (e) {
            toast.error(String(e.message).slice(0, 200));
        } finally {
            setUploading(false);
        }
    };

    const submitText = async () => {
        if (!cvText || cvText.length < 100) return toast.error("Paste at least 100 characters");
        setUploading(true);
        setOrigAts(null);
        try {
            const r = await fetch(`${API}/resume-studio/session/${session.session_id}/paste`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ text: cvText }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.detail || "Failed");
            setOrigAts(d.ats);
            toast.success(`CV received — ATS ${d.ats.overall}/100`);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setUploading(false);
        }
    };

    const runRewrite = async () => {
        setRunning(true);
        try {
            const r = await fetch(`${API}/resume-studio/session/${session.session_id}/rewrite`, {
                method: "POST",
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.detail || "Rewrite failed");
            setNewAts(d.rewritten_ats);
            onSession({ ...session, original_ats: origAts, rewritten_ats: d.rewritten_ats, mode: "rewrite" });
            onDone();
        } catch (e) {
            toast.error(String(e.message).slice(0, 200));
        } finally {
            setRunning(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto">
            <BackButton onClick={onBack} />
            <StepDots current={step} total={2} />
            {step === 1 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
                    <h2 className="text-xl font-bold">Where should we send your CV?</h2>
                    <p className="text-sm text-slate-500 mt-1">No spam. Just your resume link + tips.</p>
                    <div className="mt-5 grid gap-3">
                        <Field label="Email">
                            <input
                                data-testid="rewrite-email-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cobalt-500"
                            />
                        </Field>
                        <Field label="Target role you're applying for">
                            <input
                                data-testid="rewrite-role-input"
                                value={targetRole}
                                onChange={(e) => setTargetRole(e.target.value)}
                                placeholder="e.g. Senior Product Manager · Fintech"
                                className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cobalt-500"
                            />
                        </Field>
                    </div>
                    <button
                        onClick={createSession}
                        data-testid="rewrite-next-btn"
                        className="mt-6 inline-flex items-center gap-2 bg-cobalt-600 text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-cobalt-700"
                    >
                        Continue <ArrowRight size={16} />
                    </button>
                </div>
            )}

            {step === 2 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
                    <h2 className="text-xl font-bold">Upload your current CV</h2>
                    <p className="text-sm text-slate-500 mt-1">PDF or plain text · Max 4MB · We never store your raw CV after the session expires.</p>

                    <div className="mt-5 border-2 border-dashed border-slate-300 rounded-xl p-6 text-center">
                        <UploadSimple size={28} className="mx-auto text-slate-400" weight="duotone" />
                        <div className="mt-2 text-sm">Drag & drop your PDF here or</div>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".pdf,application/pdf,text/plain,.txt"
                            className="hidden"
                            data-testid="rewrite-file-input"
                            onChange={(e) => uploadPdf(e.target.files?.[0])}
                        />
                        <button
                            onClick={() => fileRef.current?.click()}
                            disabled={uploading}
                            data-testid="rewrite-choose-file-btn"
                            className="mt-3 inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-slate-700"
                        >
                            {uploading ? "Uploading…" : "Choose file"}
                        </button>
                    </div>

                    <div className="mt-4 text-center text-xs text-slate-400">or paste your CV text</div>
                    <textarea
                        data-testid="rewrite-paste-textarea"
                        value={cvText}
                        onChange={(e) => setCvText(e.target.value)}
                        rows={6}
                        placeholder="Paste your CV here — plain text works best."
                        className="mt-2 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    />
                    <div className="flex items-center justify-between gap-3 mt-2">
                        <button
                            onClick={submitText}
                            disabled={uploading || cvText.length < 100}
                            data-testid="rewrite-paste-submit-btn"
                            className="text-sm text-cobalt-700 font-semibold hover:underline disabled:opacity-40"
                        >
                            {uploading ? "Analyzing…" : "Use this pasted text"}
                        </button>
                        {origAts && <AtsScoreBadge score={origAts.overall} band={origAts.band} label="Current" />}
                    </div>

                    <button
                        onClick={runRewrite}
                        disabled={!origAts || running}
                        data-testid="rewrite-run-btn"
                        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-cobalt-600 to-purple-600 text-white font-semibold px-5 py-3 rounded-lg disabled:opacity-40 hover:shadow-lg transition-shadow"
                    >
                        {running ? (
                            <>
                                <Sparkle className="animate-spin" size={16} /> NaukriDaddy AI is rewriting…
                            </>
                        ) : (
                            <>
                                <Sparkle weight="fill" size={16} /> Rewrite my CV with AI
                            </>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
}

// --- Fresher flow (build from scratch) ----------------------------------
function FresherFlow({ onSession, onDone, onBack, prefill = {}, refCode = null }) {
    const [step, setStep] = useState(1); // 1..5
    const [form, setForm] = useState({
        name: prefill.name || "",
        email: prefill.email || "",
        phone: prefill.phone || "",
        city: prefill.city || "",
        linkedin: prefill.linkedin || "",
        github: prefill.github || "",
        target_role: prefill.target_role || "",
        education: [{ level: "Undergraduate", degree: "", branch: "", college: "", from_year: "", to_year: "", is_current: false, score: "", score_type: "cgpa" }],
        skills: Array.isArray(prefill.skills) ? prefill.skills.slice(0, 20) : [],
        skillsInput: "",
        projects: [],
        internships: [],
        certifications: [],
        achievements: [],
        photoFile: null,
    });
    const [session, setSession] = useState(null);
    const [generating, setGenerating] = useState(false);

    const updateField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const updateEducation = (idx, key, value) => {
        setForm((f) => {
            const edu = [...f.education];
            edu[idx] = { ...edu[idx], [key]: value };
            return { ...f, education: edu };
        });
    };
    const addEducation = () =>
        setForm((f) => ({
            ...f,
            education: [
                ...f.education,
                { level: "10th", degree: "", branch: "", college: "", from_year: "", to_year: "", is_current: false, score: "", score_type: "percentage" },
            ],
        }));
    const removeEducation = (idx) =>
        setForm((f) => ({ ...f, education: f.education.filter((_, i) => i !== idx) }));
    const addSkill = () => {
        const s = (form.skillsInput || "").trim();
        if (!s) return;
        setForm((f) => ({
            ...f,
            skills: Array.from(new Set([...f.skills, ...s.split(",").map((x) => x.trim()).filter(Boolean)])),
            skillsInput: "",
        }));
    };
    const removeSkill = (s) => setForm((f) => ({ ...f, skills: f.skills.filter((x) => x !== s) }));

    const addProject = () =>
        setForm((f) => ({ ...f, projects: [...f.projects, { title: "", tech: "", description: "" }] }));
    const updateProject = (idx, key, value) => {
        setForm((f) => {
            const projects = [...f.projects];
            projects[idx] = { ...projects[idx], [key]: value };
            return { ...f, projects };
        });
    };
    const removeProject = (idx) =>
        setForm((f) => ({ ...f, projects: f.projects.filter((_, i) => i !== idx) }));

    const addIntern = () =>
        setForm((f) => ({ ...f, internships: [...f.internships, { company: "", role: "", duration: "", description: "" }] }));
    const updateIntern = (idx, key, value) => {
        setForm((f) => {
            const internships = [...f.internships];
            internships[idx] = { ...internships[idx], [key]: value };
            return { ...f, internships };
        });
    };
    const removeIntern = (idx) =>
        setForm((f) => ({ ...f, internships: f.internships.filter((_, i) => i !== idx) }));

    const step1Valid = form.name.trim() && form.email.includes("@") && form.phone.trim();
    const step2Valid = form.target_role.trim().length >= 3;
    const step3Valid = form.education[0]?.college || form.education[0]?.degree;
    const step4Valid = form.skills.length >= 3;

    const submit = async () => {
        setGenerating(true);
        try {
            // Create session
            const device_fp = refCode ? await computeDeviceFingerprint() : null;
            const r1 = await fetch(`${API}/resume-studio/session`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: form.email, target_role: form.target_role, mode: "fresher",
                    ...(refCode ? { referral_code: refCode, device_fp } : {}),
                }),
            });
            const d1 = await r1.json();
            if (!r1.ok) throw new Error(d1.detail || "Session create failed");
            setSession(d1.session);

            // Upload optional photo (silently ignored on ATS templates by backend)
            if (form.photoFile) {
                try {
                    const fd = new FormData();
                    fd.append("file", form.photoFile);
                    await fetch(`${API}/resume-studio/session/${d1.session.session_id}/photo`, {
                        method: "POST",
                        body: fd,
                    });
                } catch (photoErr) {
                    // Non-fatal — proceed without photo
                    console.warn("Photo upload failed, continuing:", photoErr);
                }
            }

            // Build from scratch
            const payload = {
                name: form.name,
                email: form.email,
                phone: form.phone,
                city: form.city,
                linkedin: form.linkedin,
                github: form.github,
                target_role: form.target_role,
                education: form.education.filter((e) => e.degree || e.college),
                skills: form.skills,
                projects: form.projects.filter((p) => p.title || p.description),
                internships: form.internships.filter((i) => i.company || i.role),
                certifications: form.certifications.filter(Boolean),
                achievements: form.achievements.filter(Boolean),
            };
            const r2 = await fetch(
                `${API}/resume-studio/session/${d1.session.session_id}/build-from-scratch`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                },
            );
            const d2 = await r2.json();
            if (!r2.ok) {
                const msg =
                    typeof d2.detail === "string"
                        ? d2.detail
                        : d2.detail?.message || "Generation failed";
                throw new Error(msg);
            }
            onSession({ ...d1.session, rewritten_ats: d2.rewritten_ats, mode: "fresher" });
            onDone();
        } catch (e) {
            toast.error(String(e.message).slice(0, 200));
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto">
            <BackButton onClick={onBack} />
            <StepDots current={step} total={5} />

            <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
                {step === 1 && (
                    <>
                        <h2 className="text-xl font-bold">Basic details</h2>
                        <p className="text-sm text-slate-500 mt-1">All fields marked * are required.</p>
                        <div className="mt-5 grid md:grid-cols-2 gap-3">
                            <Field label="Full name *">
                                <input
                                    data-testid="fresher-name-input"
                                    value={form.name}
                                    onChange={(e) => updateField("name", e.target.value)}
                                    placeholder="Rahul Kumar"
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                                />
                            </Field>
                            <Field label="Email *">
                                <input
                                    data-testid="fresher-email-input"
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => updateField("email", e.target.value)}
                                    placeholder="you@example.com"
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                                />
                            </Field>
                            <Field label="Phone *">
                                <input
                                    data-testid="fresher-phone-input"
                                    value={form.phone}
                                    onChange={(e) => updateField("phone", e.target.value)}
                                    placeholder="+91 98765 43210"
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                                />
                            </Field>
                            <Field label="City">
                                <input
                                    data-testid="fresher-city-input"
                                    value={form.city}
                                    onChange={(e) => updateField("city", e.target.value)}
                                    placeholder="Bengaluru"
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                                />
                            </Field>
                            <Field label="LinkedIn (optional)">
                                <input
                                    data-testid="fresher-linkedin-input"
                                    value={form.linkedin}
                                    onChange={(e) => updateField("linkedin", e.target.value)}
                                    placeholder="linkedin.com/in/…"
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                                />
                            </Field>
                            <Field label="GitHub / Portfolio (optional)">
                                <input
                                    data-testid="fresher-github-input"
                                    value={form.github}
                                    onChange={(e) => updateField("github", e.target.value)}
                                    placeholder="github.com/…"
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                                />
                            </Field>
                        </div>
                        <PhotoUpload
                            file={form.photoFile}
                            onFileChange={(f) => updateField("photoFile", f)}
                        />
                        <NextButton
                            onClick={() => setStep(2)}
                            disabled={!step1Valid}
                            testid="fresher-step1-next-btn"
                        />
                    </>
                )}

                {step === 2 && (
                    <>
                        <h2 className="text-xl font-bold">Target role</h2>
                        <p className="text-sm text-slate-500 mt-1">What are you applying for?</p>
                        <div className="mt-5">
                            <Field label="Role title *">
                                <input
                                    data-testid="fresher-role-input"
                                    value={form.target_role}
                                    onChange={(e) => updateField("target_role", e.target.value)}
                                    placeholder="e.g. Software Engineer, Data Analyst, Marketing Associate"
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                                />
                            </Field>
                        </div>
                        <div className="mt-6 flex gap-3">
                            <BackStepButton onClick={() => setStep(1)} />
                            <NextButton
                                onClick={() => setStep(3)}
                                disabled={!step2Valid}
                                testid="fresher-step2-next-btn"
                            />
                        </div>
                    </>
                )}

                {step === 3 && (
                    <>
                        <h2 className="text-xl font-bold">Education *</h2>
                        <p className="text-sm text-slate-500 mt-1">Add from secondary school (10th) up to your highest degree. Score can be CGPA or percentage.</p>
                        {form.education.map((edu, idx) => (
                            <EducationBlock
                                key={idx}
                                idx={idx}
                                edu={edu}
                                testidPrefix="fresher-edu"
                                onChange={(k, v) => updateEducation(idx, k, v)}
                                onRemove={form.education.length > 1 ? () => removeEducation(idx) : null}
                                accentColor="cobalt"
                            />
                        ))}
                        {form.education.length < 6 && (
                            <button
                                onClick={addEducation}
                                data-testid="fresher-add-education-btn"
                                className="mt-3 text-xs inline-flex items-center gap-1 text-cobalt-700 font-semibold hover:underline"
                            >
                                <Plus size={12} /> Add another education (10th, 12th, PG…)
                            </button>
                        )}
                        <div className="mt-6 flex gap-3">
                            <BackStepButton onClick={() => setStep(2)} />
                            <NextButton
                                onClick={() => setStep(4)}
                                disabled={!step3Valid}
                                testid="fresher-step3-next-btn"
                            />
                        </div>
                    </>
                )}

                {step === 4 && (
                    <>
                        <h2 className="text-xl font-bold">Skills *</h2>
                        <p className="text-sm text-slate-500 mt-1">At least 3. Add languages, frameworks, tools.</p>
                        <div className="mt-4 flex gap-2">
                            <input
                                data-testid="fresher-skill-input"
                                value={form.skillsInput}
                                onChange={(e) => updateField("skillsInput", e.target.value)}
                                onKeyDown={(e) => (e.key === "Enter" || e.key === ",") && (e.preventDefault(), addSkill())}
                                placeholder="Type a skill, press Enter"
                                className="flex-1 border border-slate-300 rounded-lg px-3 py-2"
                            />
                            <button
                                onClick={addSkill}
                                data-testid="fresher-skill-add-btn"
                                className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold hover:bg-slate-700"
                            >
                                Add
                            </button>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                            {form.skills.map((s) => (
                                <span
                                    key={s}
                                    data-testid={`fresher-skill-chip-${s}`}
                                    className="inline-flex items-center gap-1 bg-cobalt-50 text-cobalt-700 px-3 py-1 rounded-full text-sm"
                                >
                                    {s}
                                    <button
                                        onClick={() => removeSkill(s)}
                                        className="hover:text-red-600"
                                        aria-label={`Remove ${s}`}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                            {form.skills.length === 0 && (
                                <span className="text-xs text-slate-400">
                                    e.g. Python, SQL, React, Communication
                                </span>
                            )}
                        </div>
                        <div className="mt-6 flex gap-3">
                            <BackStepButton onClick={() => setStep(3)} />
                            <NextButton
                                onClick={() => setStep(5)}
                                disabled={!step4Valid}
                                testid="fresher-step4-next-btn"
                            />
                        </div>
                    </>
                )}

                {step === 5 && (
                    <>
                        <h2 className="text-xl font-bold">Projects & experience (optional)</h2>
                        <p className="text-sm text-slate-500 mt-1">
                            Freshers with 1-2 solid projects or internships get 3x more interviews. Skip if you don't have any yet.
                        </p>

                        <div className="mt-5">
                            <div className="flex items-center justify-between">
                                <h3 className="font-semibold text-sm">Projects</h3>
                                <button
                                    onClick={addProject}
                                    data-testid="fresher-add-project-btn"
                                    className="text-xs inline-flex items-center gap-1 text-cobalt-700 font-semibold hover:underline"
                                >
                                    <Plus size={12} /> Add project
                                </button>
                            </div>
                            {form.projects.map((p, idx) => (
                                <ItemCard
                                    key={idx}
                                    label={`Project #${idx + 1}`}
                                    onRemove={() => removeProject(idx)}
                                    testid={`fresher-project-remove-${idx}`}
                                >
                                    <div className="grid grid-cols-1 gap-2">
                                        <input
                                            data-testid={`fresher-project-title-${idx}`}
                                            value={p.title}
                                            onChange={(e) => updateProject(idx, "title", e.target.value)}
                                            placeholder="Project title"
                                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                                        />
                                        <input
                                            data-testid={`fresher-project-tech-${idx}`}
                                            value={p.tech}
                                            onChange={(e) => updateProject(idx, "tech", e.target.value)}
                                            placeholder="Tech stack (e.g. React, Firebase)"
                                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                                        />
                                        <YearRangeInputs
                                            testidPrefix={`fresher-project-${idx}`}
                                            fromYear={p.from_year || ""}
                                            toYear={p.to_year || ""}
                                            isCurrent={!!p.is_current}
                                            onChange={(k, v) => updateProject(idx, k, v)}
                                            currentLabel="Ongoing"
                                        />
                                        <textarea
                                            data-testid={`fresher-project-desc-${idx}`}
                                            value={p.description}
                                            onChange={(e) => updateProject(idx, "description", e.target.value)}
                                            placeholder="1-2 line description of what it does + impact"
                                            rows={2}
                                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                                        />
                                    </div>
                                </ItemCard>
                            ))}
                        </div>

                        <div className="mt-6">
                            <div className="flex items-center justify-between">
                                <h3 className="font-semibold text-sm">Internships</h3>
                                <button
                                    onClick={addIntern}
                                    data-testid="fresher-add-intern-btn"
                                    className="text-xs inline-flex items-center gap-1 text-cobalt-700 font-semibold hover:underline"
                                >
                                    <Plus size={12} /> Add internship
                                </button>
                            </div>
                            {form.internships.map((i, idx) => (
                                <ItemCard
                                    key={idx}
                                    label={`Internship #${idx + 1}`}
                                    onRemove={() => removeIntern(idx)}
                                    testid={`fresher-intern-remove-${idx}`}
                                >
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <input
                                            data-testid={`fresher-intern-company-${idx}`}
                                            value={i.company}
                                            onChange={(e) => updateIntern(idx, "company", e.target.value)}
                                            placeholder="Company"
                                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                                        />
                                        <input
                                            data-testid={`fresher-intern-role-${idx}`}
                                            value={i.role}
                                            onChange={(e) => updateIntern(idx, "role", e.target.value)}
                                            placeholder="Role (e.g. SDE Intern)"
                                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                                        />
                                    </div>
                                    <YearRangeInputs
                                        testidPrefix={`fresher-intern-${idx}`}
                                        fromYear={i.from_year || ""}
                                        toYear={i.to_year || ""}
                                        isCurrent={!!i.is_current}
                                        onChange={(k, v) => updateIntern(idx, k, v)}
                                        currentLabel="Currently interning"
                                    />
                                    <textarea
                                        data-testid={`fresher-intern-desc-${idx}`}
                                        value={i.description}
                                        onChange={(e) => updateIntern(idx, "description", e.target.value)}
                                        placeholder="What did you build / learn?"
                                        rows={2}
                                        className="mt-2 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                                    />
                                </ItemCard>
                            ))}
                        </div>

                        <div className="mt-6 flex gap-3">
                            <BackStepButton onClick={() => setStep(4)} />
                            <button
                                onClick={submit}
                                disabled={generating}
                                data-testid="fresher-generate-btn"
                                className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold px-5 py-3 rounded-lg disabled:opacity-40 hover:shadow-lg transition-shadow"
                            >
                                {generating ? (
                                    <>
                                        <Sparkle className="animate-spin" size={16} /> NaukriDaddy AI is building your CV…
                                    </>
                                ) : (
                                    <>
                                        <Sparkle weight="fill" size={16} /> Generate my first CV
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// --- Experienced flow (build from scratch, 2+ yrs) ----------------------
function ExperiencedFlow({ onSession, onDone, onBack, prefill = {}, refCode = null }) {
    const [step, setStep] = useState(1); // 1..5
    const [form, setForm] = useState({
        name: prefill.name || "",
        email: prefill.email || "",
        phone: prefill.phone || "",
        city: prefill.city || "",
        linkedin: prefill.linkedin || "",
        github: prefill.github || "",
        target_role: prefill.target_role || "",
        years_experience: "",
        experience: [{ company: "", role: "", location: "", duration: "", team_size: "", direct_reports: "", bullets: "" }],
        skills: Array.isArray(prefill.skills) ? prefill.skills.slice(0, 20) : [],
        skillsInput: "",
        education: [{ level: "Undergraduate", degree: "", branch: "", college: "", from_year: "", to_year: "", is_current: false, score: "", score_type: "cgpa" }],
        projects: [],
        certifications: [],
        awards: [],
    });
    const [generating, setGenerating] = useState(false);

    const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const updateExp = (idx, key, value) => {
        setForm((f) => {
            const experience = [...f.experience];
            experience[idx] = { ...experience[idx], [key]: value };
            return { ...f, experience };
        });
    };
    const addExp = () =>
        setForm((f) => ({
            ...f,
            experience: [
                ...f.experience,
                { company: "", role: "", location: "", duration: "", team_size: "", direct_reports: "", bullets: "" },
            ],
        }));
    const removeExp = (idx) =>
        setForm((f) => ({ ...f, experience: f.experience.filter((_, i) => i !== idx) }));

    const updateEdu = (idx, key, value) => {
        setForm((f) => {
            const education = [...f.education];
            education[idx] = { ...education[idx], [key]: value };
            return { ...f, education };
        });
    };

    const addSkill = () => {
        const s = (form.skillsInput || "").trim();
        if (!s) return;
        setForm((f) => ({
            ...f,
            skills: Array.from(new Set([...f.skills, ...s.split(",").map((x) => x.trim()).filter(Boolean)])),
            skillsInput: "",
        }));
    };
    const removeSkill = (s) => setForm((f) => ({ ...f, skills: f.skills.filter((x) => x !== s) }));

    const step1Valid = form.name.trim() && form.email.includes("@") && form.phone.trim() && form.target_role.trim().length >= 3;
    const step2Valid = form.experience.some((e) => e.company && e.role);
    const step3Valid = form.skills.length >= 3;

    const submit = async () => {
        setGenerating(true);
        try {
            const r1 = await fetch(`${API}/resume-studio/session`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: form.email, target_role: form.target_role, mode: "experienced",
                    ...(refCode ? {
                        referral_code: refCode,
                        device_fp: await computeDeviceFingerprint(),
                    } : {}),
                }),
            });
            const d1 = await r1.json();
            if (!r1.ok) throw new Error(d1.detail || "Session create failed");

            // Upload optional photo (silently ignored on ATS templates)
            if (form.photoFile) {
                try {
                    const fd = new FormData();
                    fd.append("file", form.photoFile);
                    await fetch(`${API}/resume-studio/session/${d1.session.session_id}/photo`, {
                        method: "POST",
                        body: fd,
                    });
                } catch (photoErr) {
                    console.warn("Photo upload failed, continuing:", photoErr);
                }
            }

            // Turn bullets textarea into array
            const experience = form.experience
                .filter((e) => e.company && e.role)
                .map((e) => ({
                    ...e,
                    bullets: (e.bullets || "")
                        .split(/\n+/)
                        .map((b) => b.replace(/^[-•·*\s]+/, "").trim())
                        .filter(Boolean),
                }));

            const payload = {
                name: form.name,
                email: form.email,
                phone: form.phone,
                city: form.city,
                linkedin: form.linkedin,
                github: form.github,
                target_role: form.target_role,
                years_experience: form.years_experience,
                experience,
                skills: form.skills,
                education: form.education.filter((e) => e.degree || e.college),
                projects: form.projects.filter((p) => p.title || p.description),
                certifications: form.certifications.filter(Boolean),
                awards: form.awards.filter(Boolean),
            };
            const r2 = await fetch(
                `${API}/resume-studio/session/${d1.session.session_id}/build-experienced`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                },
            );
            const d2 = await r2.json();
            if (!r2.ok) {
                const msg =
                    typeof d2.detail === "string"
                        ? d2.detail
                        : d2.detail?.message || "Generation failed";
                throw new Error(msg);
            }
            onSession({ ...d1.session, rewritten_ats: d2.rewritten_ats, mode: "experienced" });
            onDone();
        } catch (e) {
            toast.error(String(e.message).slice(0, 200));
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto">
            <BackButton onClick={onBack} />
            <StepDots current={step} total={4} />
            <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
                {step === 1 && (
                    <>
                        <h2 className="text-xl font-bold">Basic details & target role</h2>
                        <p className="text-sm text-slate-500 mt-1">
                            We'll never invent anything — you keep full control of what goes on your CV.
                        </p>
                        <div className="mt-5 grid md:grid-cols-2 gap-3">
                            <Field label="Full name *">
                                <input
                                    data-testid="exp-name-input"
                                    value={form.name}
                                    onChange={(e) => updateField("name", e.target.value)}
                                    placeholder="Priya Sharma"
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                                />
                            </Field>
                            <Field label="Email *">
                                <input
                                    data-testid="exp-email-input"
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => updateField("email", e.target.value)}
                                    placeholder="you@example.com"
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                                />
                            </Field>
                            <Field label="Phone *">
                                <input
                                    data-testid="exp-phone-input"
                                    value={form.phone}
                                    onChange={(e) => updateField("phone", e.target.value)}
                                    placeholder="+91 98765 43210"
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                                />
                            </Field>
                            <Field label="City">
                                <input
                                    data-testid="exp-city-input"
                                    value={form.city}
                                    onChange={(e) => updateField("city", e.target.value)}
                                    placeholder="Bengaluru"
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                                />
                            </Field>
                            <Field label="LinkedIn (optional)">
                                <input
                                    data-testid="exp-linkedin-input"
                                    value={form.linkedin}
                                    onChange={(e) => updateField("linkedin", e.target.value)}
                                    placeholder="linkedin.com/in/..."
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                                />
                            </Field>
                            <Field label="GitHub / Portfolio (optional)">
                                <input
                                    data-testid="exp-github-input"
                                    value={form.github}
                                    onChange={(e) => updateField("github", e.target.value)}
                                    placeholder="github.com/..."
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                                />
                            </Field>
                            <Field label="Target role you're applying for *">
                                <input
                                    data-testid="exp-role-input"
                                    value={form.target_role}
                                    onChange={(e) => updateField("target_role", e.target.value)}
                                    placeholder="e.g. Senior Product Manager · Fintech"
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                                />
                            </Field>
                            <Field label="Years of experience">
                                <input
                                    data-testid="exp-years-input"
                                    value={form.years_experience}
                                    onChange={(e) => updateField("years_experience", e.target.value)}
                                    placeholder="e.g. 6.5"
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                                />
                            </Field>
                        </div>
                        <PhotoUpload
                            file={form.photoFile}
                            onFileChange={(f) => updateField("photoFile", f)}
                        />
                        <NextButton onClick={() => setStep(2)} disabled={!step1Valid} testid="exp-step1-next-btn" />
                    </>
                )}

                {step === 2 && (
                    <>
                        <h2 className="text-xl font-bold">Work experience *</h2>
                        <p className="text-sm text-slate-500 mt-1">
                            Add each role. Paste 3-5 roles & responsibilities (one per line) — AI will convert them into strong impact bullets. Include real metrics where possible.
                        </p>
                        {form.experience.map((exp, idx) => (
                            <ItemCard
                                key={idx}
                                label={`Role #${idx + 1}`}
                                onRemove={form.experience.length > 1 ? () => removeExp(idx) : null}
                                testid={`exp-experience-remove-${idx}`}
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <input
                                        data-testid={`exp-experience-company-${idx}`}
                                        value={exp.company}
                                        onChange={(e) => updateExp(idx, "company", e.target.value)}
                                        placeholder="Company"
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                                    />
                                    <input
                                        data-testid={`exp-experience-role-${idx}`}
                                        value={exp.role}
                                        onChange={(e) => updateExp(idx, "role", e.target.value)}
                                        placeholder="Your role / title"
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                                    />
                                    <input
                                        data-testid={`exp-experience-location-${idx}`}
                                        value={exp.location}
                                        onChange={(e) => updateExp(idx, "location", e.target.value)}
                                        placeholder="Location (e.g., Bengaluru)"
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                                    />
                                    <input
                                        data-testid={`exp-experience-team-${idx}`}
                                        value={exp.team_size}
                                        onChange={(e) => updateExp(idx, "team_size", e.target.value)}
                                        placeholder="Team size (optional)"
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                                    />
                                    <input
                                        data-testid={`exp-experience-reports-${idx}`}
                                        value={exp.direct_reports}
                                        onChange={(e) => updateExp(idx, "direct_reports", e.target.value)}
                                        placeholder="Direct reports (optional)"
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                                    />
                                </div>
                                <YearRangeInputs
                                    testidPrefix={`exp-experience-${idx}`}
                                    fromYear={exp.from_year || ""}
                                    toYear={exp.to_year || ""}
                                    isCurrent={!!exp.is_current}
                                    onChange={(k, v) => updateExp(idx, k, v)}
                                    currentLabel="I currently work here"
                                />
                                <textarea
                                    data-testid={`exp-experience-bullets-${idx}`}
                                    value={exp.bullets}
                                    onChange={(e) => updateExp(idx, "bullets", e.target.value)}
                                    rows={5}
                                    placeholder={"Roles & responsibilities — one per line, e.g.\nOwned retail investment product line, grew MAU 4x YoY\nLed 4-engineer team through 14 A/B tests, 6 winners moved KPIs 8-30%\nLaunched auto-invest feature driving 22% incremental revenue"}
                                    className="mt-3 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                                />
                            </ItemCard>
                        ))}
                        <button
                            onClick={addExp}
                            data-testid="exp-add-experience-btn"
                            className="mt-3 text-xs text-cobalt-700 font-semibold hover:underline inline-flex items-center gap-1"
                        >
                            <Plus size={12} /> Add another role
                        </button>
                        <div className="mt-6 flex gap-3">
                            <BackStepButton onClick={() => setStep(1)} />
                            <NextButton onClick={() => setStep(3)} disabled={!step2Valid} testid="exp-step2-next-btn" />
                        </div>
                    </>
                )}

                {step === 3 && (
                    <>
                        <h2 className="text-xl font-bold">Skills *</h2>
                        <p className="text-sm text-slate-500 mt-1">At least 3. Add languages, frameworks, tools, methodologies.</p>
                        <div className="mt-4 flex gap-2">
                            <input
                                data-testid="exp-skill-input"
                                value={form.skillsInput}
                                onChange={(e) => updateField("skillsInput", e.target.value)}
                                onKeyDown={(e) => (e.key === "Enter" || e.key === ",") && (e.preventDefault(), addSkill())}
                                placeholder="Type a skill, press Enter"
                                className="flex-1 border border-slate-300 rounded-lg px-3 py-2"
                            />
                            <button
                                onClick={addSkill}
                                data-testid="exp-skill-add-btn"
                                className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold hover:bg-slate-700"
                            >
                                Add
                            </button>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                            {form.skills.map((s) => (
                                <span
                                    key={s}
                                    data-testid={`exp-skill-chip-${s}`}
                                    className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-sm"
                                >
                                    {s}
                                    <button
                                        onClick={() => removeSkill(s)}
                                        className="hover:text-red-600"
                                        aria-label={`Remove ${s}`}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                            {form.skills.length === 0 && (
                                <span className="text-xs text-slate-400">
                                    e.g. Product Strategy, SQL, A/B testing, Amplitude
                                </span>
                            )}
                        </div>
                        <div className="mt-6 flex gap-3">
                            <BackStepButton onClick={() => setStep(2)} />
                            <NextButton onClick={() => setStep(4)} disabled={!step3Valid} testid="exp-step3-next-btn" />
                        </div>
                    </>
                )}

                {step === 4 && (
                    <>
                        <h2 className="text-xl font-bold">Education (optional)</h2>
                        <p className="text-sm text-slate-500 mt-1">Add each qualification separately — schools, undergrad, postgrad. Skip if not relevant.</p>
                        {form.education.map((edu, idx) => (
                            <EducationBlock
                                key={idx}
                                idx={idx}
                                edu={edu}
                                testidPrefix="exp-edu"
                                onChange={(k, v) => updateEdu(idx, k, v)}
                                onRemove={form.education.length > 1 ? () => setForm((f) => ({ ...f, education: f.education.filter((_, i) => i !== idx) })) : null}
                                accentColor="purple"
                            />
                        ))}
                        {form.education.length < 6 && (
                            <button
                                onClick={() => setForm((f) => ({
                                    ...f,
                                    education: [
                                        ...f.education,
                                        { level: "10th", degree: "", branch: "", college: "", from_year: "", to_year: "", is_current: false, score: "", score_type: "percentage" },
                                    ],
                                }))}
                                data-testid="exp-add-education-btn"
                                className="mt-3 text-xs inline-flex items-center gap-1 text-purple-700 font-semibold hover:underline"
                            >
                                <Plus size={12} /> Add another education
                            </button>
                        )}
                        <div className="mt-6 flex gap-3">
                            <BackStepButton onClick={() => setStep(3)} />
                            <button
                                onClick={submit}
                                disabled={generating}
                                data-testid="exp-generate-btn"
                                className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white font-semibold px-5 py-3 rounded-lg disabled:opacity-40 hover:shadow-lg transition-shadow"
                            >
                                {generating ? (
                                    <>
                                        <Sparkle className="animate-spin" size={16} /> NaukriDaddy AI is writing your CV…
                                    </>
                                ) : (
                                    <>
                                        <Sparkle weight="fill" size={16} /> Generate my CV
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

